import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiAlertCircle, FiClock, FiCreditCard } from 'react-icons/fi';
import GradientButton from '../components/ui/GradientButton';
import GradientText from '../components/ui/GradientText';
import GlassCard from '../components/ui/GlassCard';
import { AuthContext } from '../context/AuthContext';
import { 
  initiatePayment
} from '../services/paymentService';

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'processing', 'success', 'failed'
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Get payment details from location state or use defaults
  const paymentDetails = location.state?.paymentDetails || {
    planName: 'Pro Plan',
    planPrice: '₹4,999',
    planDuration: 'month',
    planId: 'plan_pro',
    isSubscription: true};

  // Extract numeric amount (remove ₹ and commas)
  const numericAmount = parseInt(paymentDetails.planPrice.replace(/[^0-9]/g, ''));

  // Check authentication and redirect if not logged in (but don't redirect if already processing payment)
  useEffect(() => {
    if (!user && !isProcessing) {
      const timer = setTimeout(() => {
        navigate('/auth/employer', {
          state: { from: '/payment', paymentDetails }
        });
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [user, navigate, paymentDetails, isProcessing]);

  // No need to pre-load config for PhonePe
  // Config is loaded when payment is initiated

  // Handle payment with PhonePe
  const handlePayment = async () => {
    if (!user) {
      setError('Please login to continue with payment');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setPaymentStatus('processing');

    try {
      // Initiate PhonePe payment (this will redirect to PhonePe's page)
      const paymentResult = await initiatePayment({
        amount: numericAmount,
        planName: paymentDetails.planName,
        planId: paymentDetails.planId,
        email: user?.email || '',
        phone: user?.phone || ''
      });

      if (!paymentResult.success) {
        throw new Error(paymentResult.error || 'Payment failed');
      }

      // If we reach here, payment redirection was successful
      // User will be redirected to PhonePe's payment page
      // PhonePe will redirect back after payment completion

    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err?.message || err?.error || 'Payment failed. Please try again.';
      setError(errorMessage);
      setPaymentStatus('failed');
      setIsProcessing(false);
    }
  };

  // Handle back button
  const handleBack = () => {
    navigate(-1);
  };

  // Handle retry payment
  const handleRetry = () => {
    setPaymentStatus(null);
    setIsProcessing(false);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button 
            onClick={handleBack}
            className="mr-4 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
            aria-label="Go back"
          >
            <FiArrowLeft size={20} className="text-gray-600 dark:text-gray-300" />
          </button>
          <div>
            <GradientText variant="primary" as="h1" className="text-2xl font-bold">
              Complete Payment
            </GradientText>
            <p className="text-gray-600 dark:text-gray-300">
              {paymentDetails.isSubscription 
                ? `Subscribe to ${paymentDetails.planName}` 
                : 'Purchase AI Credits'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Payment Section */}
          <div className="md:col-span-2">
            {paymentStatus === null && (
              <GlassCard variant="light" className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                    <FiCreditCard size={24} />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                      PhonePe Payment
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Pay securely with UPI, Cards, NetBanking & more
                    </p>
                  </div>
                </div>

                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-6 rounded-lg mb-6">
                  <div className="text-center mb-6">
                    <p className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                      {paymentDetails.planPrice}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      One-time payment for {paymentDetails.planName}
                    </p>
                  </div>

                  <div className="bg-white dark:bg-dark-800 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <FiCheckCircle size={20} className="text-green-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                          Payment Methods Available:
                        </p>
                        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          <li>• UPI (Google Pay, PhonePe, Paytm, etc.)</li>
                          <li>• Credit & Debit Cards (Visa, Mastercard, RuPay)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {!user && (
                  <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-400">
                      Please login to continue with payment. Redirecting to login page...
                    </p>
                  </div>
                )}

                {error && user && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {error}
                    </p>
                  </div>
                )}

                <GradientButton
                  variant="primary"
                  size="lg"
                  className="shadow-md w-full"
                  onClick={handlePayment}
                  disabled={isProcessing || !user}
                >
                  {isProcessing ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Pay Now'
                  )}
                </GradientButton>

                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <FiCheckCircle size={14} className="text-green-500" />
                  <span>Secure payment powered by PhonePe</span>
                </div>
              </GlassCard>
            )}

            {/* Processing Payment */}
            {paymentStatus === 'processing' && (
              <GlassCard variant="light" className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                      <FiClock size={32} className="text-indigo-500 animate-pulse" />
                    </div>
                    <div className="absolute inset-0 rounded-full border-4 border-t-indigo-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
                  </div>
                </div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-3">
                  Processing Payment
                </h2>
                <p className="text-gray-500 dark:text-gray-400">
                  Please complete the payment in the PhonePe window...
                </p>
              </GlassCard>
            )}

            {/* Payment Success */}
            {paymentStatus === 'success' && (
              <GlassCard variant="light" className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <FiCheckCircle size={32} className="text-green-500" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-3">
                  Payment Successful!
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Your {paymentDetails.planName} has been activated successfully.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Redirecting to dashboard...
                </p>
              </GlassCard>
            )}

            {/* Payment Failed */}
            {paymentStatus === 'failed' && (
              <GlassCard variant="light" className="p-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <FiAlertCircle size={32} className="text-red-500" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-3">
                  Payment Failed
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {error || 'We couldn\'t process your payment. Please try again.'}
                </p>
                <div className="flex justify-center gap-4">
                  <GradientButton
                    variant="light"
                    size="md"
                    className="border border-gray-200 dark:border-gray-700"
                    onClick={handleBack}
                  >
                    Go Back
                  </GradientButton>
                  <GradientButton
                    variant="primary"
                    size="md"
                    className="shadow-md"
                    onClick={handleRetry}
                  >
                    Try Again
                  </GradientButton>
                </div>
              </GlassCard>
            )}
          </div>

          {/* Order Summary */}
          <div className="md:col-span-1">
            <GlassCard variant="light" className="p-6 sticky top-6">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
                Order Summary
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Plan</span>
                  <span className="font-medium text-gray-800 dark:text-white">
                    {paymentDetails.planName}
                  </span>
                </div>
                
                {paymentDetails.isSubscription && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">Billing</span>
                    <span className="font-medium text-gray-800 dark:text-white">Monthly</span>
                  </div>
                )}
                
                <div className="pt-4 border-t border-gray-100 dark:border-gray-800 flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Total</span>
                  <span className="font-bold text-xl text-gray-800 dark:text-white">
                    {paymentDetails.planPrice}
                  </span>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-3">
                <div className="flex items-start gap-3">
                  <FiCheckCircle size={16} className="text-green-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">
                      Secure Payment
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      256-bit SSL encryption
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <FiCheckCircle size={16} className="text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">
                      Instant Activation
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Access features immediately
                    </p>
                  </div>
                </div>
                
                {paymentDetails.isSubscription && (
                  <div className="flex items-start gap-3">
                    <FiCheckCircle size={16} className="text-purple-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-white">
                        Cancel Anytime
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        No long-term commitment
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </GlassCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
